import React from 'react';
import { Stack, Typography } from '@mui/material';
import { Form } from 'common/form';
import { ToastType, useToast } from 'services/toast';
import { AnalysingLoadingGif } from 'common/animations/AnalysingLoadingGif';
import { ReviewInvitationsCandidateItem } from 'modules/sourcing-shared/shared-steps/SourcingSharedReviewInvitationsStep';
import { Channels } from 'generated-graphql/types';
import { useDrawerV2 } from 'common/drawer/hooks/useDrawerV2';
import { getSourcingSharedInvitationMessageFields } from '../forms/fields/getSourcingSharedInvitationMessageFields';
import {
  SourcingInviteCandidateEmailFormData,
  sourcingInviteCandidateEmailSchema,
  SourcingInviteCandidateSmsFormData,
  sourcingInviteCandidateSmsSchema
} from '../forms/schema/sourcingSharedInviteMessageSchema';

type SourcingSharedPreviewInvitationTabProps = {
  channel: Channels;
  subject: string | undefined;
  message: string;
  candidate: ReviewInvitationsCandidateItem;
  loading?: boolean;
  onUpdateInvitationMessage: (
    candidateId: string,
    customMessage: string
  ) => Promise<void>;
};

export const SourcingSharedPreviewInvitationTab: React.FC<
  SourcingSharedPreviewInvitationTabProps
> = ({
  subject,
  channel,
  message,
  candidate,
  loading = false,
  onUpdateInvitationMessage
}) => {
  const { closeDrawer } = useDrawerV2();
  const { showToast } = useToast();
  const [isUpdating, setIsUpdating] = React.useState(false);

  const handleSubmit = async (
    values:
      | SourcingInviteCandidateSmsFormData
      | SourcingInviteCandidateEmailFormData
  ) => {
    try {
      setIsUpdating(true);
      await onUpdateInvitationMessage(
        candidate.id,
        values.customTemplatedMessage
      );

      showToast({
        title: `${candidate.fullName}'s invitation has been updated`,
        type: ToastType.Success
      });

      closeDrawer();
    } catch (error) {
      showToast({
        message: 'Failed to update invitation message',
        type: ToastType.Error
      });
    } finally {
      setIsUpdating(false);
    }
  };

  if (loading) {
    return (
      <Stack direction="row" alignItems="center" gap={1}>
        <AnalysingLoadingGif />
        <Typography fontWeight={600} fontSize="24px">
          Generating personalised message
        </Typography>
      </Stack>
    );
  }

  return (
    <Stack direction="column" gap="20px">
      {channel === Channels.Email && (
        <Typography sx={{ fontSize: '20px' }}>Subject: {subject}</Typography>
      )}
      <Form
        schema={
          channel === Channels.Sms
            ? sourcingInviteCandidateSmsSchema
            : sourcingInviteCandidateEmailSchema
        }
        sx={{
          width: '600px'
        }}
        fields={getSourcingSharedInvitationMessageFields({
          candidateFullName: candidate.fullName,
          customInvitationMessageDefaultValue:
            candidate.customInvitationMessage || message,
          channelType: channel
        })}
        submitButtonProps={{
          label: 'Save changes',
          loading: isUpdating
        }}
        onSubmit={handleSubmit}
        onCancel={() => closeDrawer()}
      />
    </Stack>
  );
};
